import { Autocomplete, Avatar, Button, Checkbox, FormControlLabel } from "@mui/material";
import {
  Box,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { Form, FormikProvider, useFormik } from "formik";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ApiService from "../../services/categoryManagementApi";
import CardLayout from "../../layouts/CardLayout";
import errorHandler from "../../utils/errorHandler";
import { handleNumberInput } from "../../utils/CommonFunction";
import { Helmet } from "react-helmet-async";
import { LoadingButton } from "@mui/lab";
import { productValidation } from "../../utils/Schema";
import { IMAGE_BASE } from "../../utils/constants";

const { productManagementApi } = ApiService;

const AddProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [allCategories, setAllCategories] = useState([]);
  const [selectedCategoryName, setSelectedCategoryName] = useState("");
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [data, setData] = useState();
  const [includeWeight, setIncludeWeight] = useState(false);

  const fetchTaskData = useCallback(async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await productManagementApi.getById({ id });
        const resData = response?.data?.data;
        setImagePreview(IMAGE_BASE + resData?.image);
        setData(resData);
        if (resData?.weight) {
          setIncludeWeight(true);
        }
      } catch (error) {
        errorHandler(error);
      } finally {
        setLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchTaskData();
  }, [fetchTaskData]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      imagePath: "product",
      name: data?.name || "",
      nameKh: data?.nameKh || "",
      qty: data?.qty || "",
      productModel: data?.productModel || "",
      weight: data?.weight || "",
      includeWeight: Boolean(data?.weight),
      price: data?.price || "",
      image: IMAGE_BASE + data?.image || "",
      productId: data?.productId || "",
      description: data?.description || "",
      descriptionKh: data?.descriptionKh || "",
      categoryId: data?.categoryId?._id || "",
      selectedCategoryName: data?.categoryId?.name || "",
      galleryImages: [],
      oldGalleryImages: [],
      deletedGalleryImages: [],
      isFreeProduct: data?.isFreeProduct || false,
      redeemRewardPoint: data?.redeemRewardPoint || "",
      index: data?.index || "",
    },
    validationSchema: productValidation,
    onSubmit: (values, { setSubmitting }) => {
      handleSumbit({ ...values }, setSubmitting);
    },
  });

  const handleSumbit = async (values, setSubmitting) => {
    try {
      const formData = new FormData();
      if (id) {
        formData.append("id", id);
      }
      formData.append("imagePath", values.imagePath);
      formData.append("name", values.name);
      formData.append("nameKh", values.nameKh);
      formData.append("qty", values.qty);
      formData.append("productModel", values.productModel);

      formData.append("includeWeight", values.includeWeight);
      if (values.includeWeight === true) {
        formData.append("weight", values.weight);
      }
      formData.append("price", values.price);
      formData.append("description", values.description);
      formData.append("descriptionKh", values.descriptionKh);
      formData.append("categoryId", values.categoryId);
      formData.append("isFreeProduct", values.isFreeProduct);
      formData.append("index", Number(values?.index));
      formData.append("redeemRewardPoint", values.redeemRewardPoint);
      if (selectedImage) {
        formData.append("image", selectedImage);
      }
      for (const gimage of values.galleryImages) {
        formData.append("galleryImages", gimage);
      }

      if (id) {
        for (const i of values.oldGalleryImages) {
          formData.append("oldGalleryImages[]", i);
        }
        for (const i of values.deletedGalleryImages) {
          formData.append("deletedGalleryImages[]", i);
        }
      }

      let res = id
        ? await productManagementApi.updateProduct(formData)
        : await productManagementApi.add(formData);
      toast.success(res?.data?.message, { toastId: "success" });
      navigate(-1);

      fileInputRef.current.value = "";
    } catch (error) {
      errorHandler(error);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (data && data.categoryId) {
      setSelectedCategoryName(data.categoryId.name || "");
    }
  }, [data]);

  const handleImageChange = (event) => {
    const file = event.currentTarget.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/gif", "image/jpg"];

    if (selectedImage && selectedImage.name === file?.name) {
      toast.error("You cannot upload the same image again.");
      fileInputRef.current.value = "";
      return;
    }

    if (file && validTypes.includes(file.type)) {
      setImageLoading(true);
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
      formik.setFieldValue("image", file);
      setTimeout(() => {
        setImageLoading(false);
      }, 2000);
    } else {
      toast.error("Please upload a valid image (JPEG, JPG, PNG, or GIF).");
      fileInputRef.current.value = "";
    }
  };

  const { errors, touched, getFieldProps } = formik;

  const fetchAllCategories = async () => {
    try {
      const payload = {
        pageNumber: 1,
        pageSize: 5,
        searchItem: "",
        status: [],
      };
      let response = await productManagementApi.getAllCategoryForProducts(
        payload
      );
      setAllCategories(response?.data?.data?.result);
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    fetchAllCategories();
  }, []);

  const { values } = formik;

  return (
    <Box>
      <Helmet>
        <title>{id ? "Edit" : "Add"} Product</title>
      </Helmet>
      <CardLayout>
        <CardHeader title={id ? "Edit Product" : "Add Product"} />
        <CardContent>
          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <input
                    accept="image/*"
                    type="file"
                    ref={fileInputRef}
                    onChange={handleImageChange}
                    style={{ display: "none" }}
                  />

                  <Box
                    mt={2}
                    onClick={() => fileInputRef.current.click()}
                    className="upload-box"
                  >
                    {imageLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      <Avatar
                        src={imagePreview || "/path/to/default-avatar.jpg"}
                        alt="preview"
                        className="avatar"
                      />
                    )}
                  </Box>

                  <Box mt={2} display="flex" justifyContent="center">
                    <Button
                      size="large"
                      variant="contained"
                      sx={{ ml: 3, mr: 3 }}
                      onClick={() => fileInputRef.current.click()}
                    >
                      {id ? "Edit" : imagePreview ? "Change" : "Upload"} Image
                    </Button>

                  </Box>

                  {formik.touched.image && formik.errors.image ? (
                    <div className="imageerror">
                      {formik.touched.image && formik.errors.image}
                    </div>
                  ) : null}
                </Grid>

                {id && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Product ID"
                      value={values?.productId}
                      disabled
                      error={Boolean(touched.productId && errors.productId)}
                      helperText={touched.productId && errors.productId}
                      {...getFieldProps("productId")}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name (English)"
                    value={values?.name}
                    required
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    {...getFieldProps("name")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Name (Khmer)"
                    value={values?.nameKh}
                    required
                    error={Boolean(touched.nameKh && errors.nameKh)}
                    helperText={touched.nameKh && errors.nameKh}
                    {...getFieldProps("nameKh")}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Product Model"
                    required
                    type="text"
                    error={Boolean(touched.productModel && errors.productModel)}
                    helperText={touched.productModel && errors.productModel}
                    {...getFieldProps("productModel")}
                   
                  />
                </Grid>
                <Grid item xs={6}>
                  <Autocomplete
                    options={allCategories || []}
                    getOptionLabel={(option) => option.name || ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    value={
                      allCategories?.find(
                        (category) => category._id === formik.values.categoryId
                      ) || null
                    }
                    onChange={(event, value) => {
                      const selectedCategoryId = value?._id || "";
                      const selectedCategoryName = value?.name || "";
                      formik.setFieldValue("categoryId", selectedCategoryId);
                      formik.setFieldValue("selectedCategoryName", selectedCategoryName);

                      setSelectedCategoryName(selectedCategoryName);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Category *"
                        error={Boolean(touched.categoryId && errors.categoryId)}
                        helperText={touched.categoryId && errors.categoryId}
                        InputLabelProps={{
                          style: {
                            color: `${touched.categoryId && errors.categoryId ? "#FF4842" : ""
                              }`,
                          },
                        }}
                      />
                    )}
                  />
                  <Grid item xs={12}>
                    <label style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <input
                        type="checkbox"
                        name="includeWeight"
                        checked={formik.values.includeWeight}
                        onChange={formik.handleChange}
                      />
                      Include weight
                    </label>
                  </Grid>
                </Grid>
                {formik.values.includeWeight && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Weight"
                      required
                      type="text"
                      error={Boolean(touched.weight && errors.weight)}
                      helperText={touched.weight && errors.weight}
                      {...getFieldProps("weight")}
                      inputProps={{ maxLength: 7 }}
                    />
                  </Grid>

                )}

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Price"
                    required
                    type="text"
                    error={Boolean(touched.price && errors.price)}
                    helperText={touched.price && errors.price}
                    {...getFieldProps("price")}
                    onChange={(e) =>
                      handleNumberInput(e, "price", formik, true, 5)
                    }
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Quantity"
                    required
                    type="text"
                    error={Boolean(touched.qty && errors.qty)}
                    helperText={touched.qty && errors.qty}
                    {...getFieldProps("qty")}
                    onChange={(e) =>
                      handleNumberInput(e, "qty", formik, false, 8)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Product Position"
                    required
                    value={formik.values.index}
                    onChange={(e) => {
                      const onlyNums = e.target.value.replace(/\D/g, '');
                      formik.setFieldValue("index", onlyNums);
                    }}
                    name="index"
                    error={Boolean(formik.touched.index && formik.errors.index)}
                    helperText={formik.touched.index && formik.errors.index}
                  />

                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Description (English)"
                    required
                    error={Boolean(touched.description && errors.description)}
                    helperText={touched.description && errors.description}
                    {...getFieldProps("description")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    label="Description (Khmer)"
                    required
                    error={Boolean(touched.descriptionKh && errors.descriptionKh)}
                    helperText={touched.descriptionKh && errors.descriptionKh}
                    {...getFieldProps("descriptionKh")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={values.isFreeProduct}
                        {...getFieldProps("isFreeProduct")}
                        color="primary"
                      />
                    }
                    label="Use as Free Product"
                  />
                </Grid>
                {values.isFreeProduct && (
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Reward Points"
                      type="number"
                      value={values.redeemRewardPoint}
                      error={Boolean(touched.redeemRewardPoint && errors.redeemRewardPoint)}
                      helperText={touched.redeemRewardPoint && errors.redeemRewardPoint}
                      {...getFieldProps("redeemRewardPoint")}
                    />
                  </Grid>
                )}


                <Grid item xs={12} justifyContent="center" display="flex">
                  <Grid item xs={6} >
                    <LoadingButton
                      fullWidth
                      size="large"
                      type="submit"
                      variant="contained"
                      loading={loading}
                    >
                      {id ? "Edit" : "Add"} Product
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </CardLayout>
    </Box>
  );
};

export default AddProduct;
